import { LOCALE_NAMES } from "@repo/shared";
import type { FlowStatus, LocaleCode } from "@repo/shared/types";
import { Copy, Pencil, Trash2 } from "lucide-react";
import { CloneJobRow } from "@/components/clone-jobs";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Switch } from "@/components/ui/switch";
import {
  rowControl,
  Table,
  TableBody,
  TableCell,
  TableCellLink,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useConfirm } from "@/hooks/use-confirm";
import { ApiError } from "@/lib/api/_client";
import { useListedCloneJobs } from "@/lib/hooks/use-clone-jobs";
import {
  getFlowInstanceCount,
  useDeleteFlow,
  useSetFlowStatus,
  useToggleSupportedForFollowup,
} from "@/lib/hooks/use-flows";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import type { Flow } from "@/lib/types/flow";
import { cn } from "@/lib/utils";
import { FlowStatusToggle } from "./flow-status-toggle";
import { T } from "./flows-table.i18n";

type FlowsTableProps = {
  flows: Flow[];
  onEdit: (flow: Flow) => void;
  onClone: (flow: Flow) => void;
};

export function FlowsTable({ flows, onEdit, onClone }: FlowsTableProps) {
  const { userLanguage } = useUserLanguage();
  const t = useT(T);
  const { confirm, ConfirmationDialog } = useConfirm();
  // QL-334 — translated copies still being made; a finished one is a real row.
  const cloneJobs = useListedCloneJobs("flow");

  const statusMutation = useSetFlowStatus();
  const toggleFollowupMutation = useToggleSupportedForFollowup();
  const deleteMutation = useDeleteFlow();
  const busy =
    statusMutation.isPending || toggleFollowupMutation.isPending || deleteMutation.isPending;

  // The server validates each toggle's rules and explains a refusal (QL-401:
  // followup enable requires an active flow, a collection and the
  // goal/portfolio page structure); this only relays the message.
  const makeToggleHandler =
    (mutate: (id: string) => Promise<unknown>, title: string) => async (id: string) => {
      try {
        await mutate(id);
      } catch (err) {
        const message = err instanceof ApiError ? err.message : t.error;
        await confirm({
          title,
          description: message,
          confirmText: t.ok,
          variant: "destructive",
          type: "alert",
        });
      }
    };
  // QL-239 — the server validates a move into active and explains a refusal.
  const handleStatusChange = (id: string, status: FlowStatus) =>
    makeToggleHandler(() => statusMutation.mutateAsync({ id, status }), t.cannotToggleFlow)(id);
  const handleToggleFollowup = makeToggleHandler(
    toggleFollowupMutation.mutateAsync,
    t.cannotToggleFollowup,
  );

  const handleDelete = async (id: string, name: string) => {
    const instanceCount = await getFlowInstanceCount(id);
    const description =
      instanceCount > 0
        ? t.deleteFlowWithInstances(name, String(instanceCount))
        : t.deleteFlowConfirmation(name);

    const confirmed = await confirm({
      title: t.deleteFlow,
      description,
      confirmText: t.delete,
      cancelText: t.cancel,
      variant: "destructive",
    });

    if (!confirmed) return;

    try {
      await deleteMutation.mutateAsync(id);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : t.failedToDeleteFlow;
      await confirm({
        title: t.error,
        description: message,
        confirmText: t.ok,
        variant: "destructive",
        type: "alert",
      });
    }
  };

  if (flows.length === 0 && cloneJobs.length === 0) {
    return <EmptyState>{t.noFlowsYet}</EmptyState>;
  }

  return (
    <>
      <Table variant="navigation">
        <TableHeader>
          <TableRow>
            <TableHead>{t.name}</TableHead>
            <TableHead className="w-28">{t.language}</TableHead>
            <TableHead className="whitespace-nowrap">{t.multiGoal}</TableHead>
            <TableHead className="whitespace-nowrap">{t.multiPortfolio}</TableHead>
            <TableHead className="w-24">{t.followup}</TableHead>
            <TableHead className="whitespace-nowrap">{t.status}</TableHead>
            <TableHead className="w-32 text-right">{t.actions}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {cloneJobs.map((job) => (
            <CloneJobRow key={job.id} job={job} colSpan={7} />
          ))}
          {flows.map((flow) => (
            <TableRow key={flow.id}>
              <TableCellLink to={`/settings/flows/${flow.id}`}>
                <div className="font-medium">{flow.name}</div>
                {flow.description && (
                  <p className="text-sm text-muted-foreground mt-1">{flow.description}</p>
                )}
              </TableCellLink>
              <TableCell className="text-sm text-muted-foreground">
                {flow.language ? LOCALE_NAMES[flow.language as LocaleCode] || flow.language : "-"}
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {flow.multiGoal ? t.yes : t.no}
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {flow.multiPortfolio ? t.yes : t.no}
              </TableCell>
              <TableCell className={rowControl}>
                <Switch
                  aria-label={`${t.followup}: ${flow.name}`}
                  checked={flow.supportedForFollowup}
                  onCheckedChange={() => handleToggleFollowup(flow.id)}
                  disabled={busy}
                />
              </TableCell>
              <TableCell className={rowControl}>
                <FlowStatusToggle
                  ariaLabel={`${t.status}: ${flow.name}`}
                  value={flow.status}
                  onChange={(status) => handleStatusChange(flow.id, status)}
                  language={userLanguage}
                  disabled={busy}
                  size="sm"
                />
              </TableCell>
              <TableCell className={cn("text-right", rowControl)}>
                <div className="flex items-center justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onEdit(flow)}
                    disabled={busy}
                    tooltip={t.edit}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onClone(flow)}
                    disabled={busy}
                    tooltip={t.clone}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(flow.id, flow.name)}
                    disabled={busy}
                    tooltip={t.delete}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <ConfirmationDialog />
    </>
  );
}
